/*
  assets/cart-recommendations.js  —  v3
  Bear Hollow Supply · 2026-10-01

  The theme prints one <cart-recommendations-component> under every
  cart line (snippets/cart-drawer-item.liquid). Stock behavior was
  for each one to load its own "You may also like" carousel, so a
  3-item cart showed 3 carousels.

  Now the components do not load on their own. One coordinator runs
  per cart render and shows a SINGLE block:

  - Where:  in the last line's component, so it sits at the bottom
            of the item list. All other components stay empty and
            hidden.
  - What:   suggestions for the most expensive item in the cart,
            judged by the price of one unit (read from the
            data-wpd-cart-drawer-variant-price attribute the cart
            line already carries).
  - Source: Search & Discovery "complementary" picks first. If the
            item has none, falls back to Shopify's "related".
  - Products already in the cart are filtered out by
    sections/cart-recommendations.liquid.

  Nothing here changes cart lines, quantities, totals or checkout.

  v3:
  - The block is made visible BEFORE its HTML is inserted, so the
    slider measures a visible element.
  - <add-to-cart-button> (the "Add" button on a suggestion) is the
    theme's own element; the only change is that a failed add now
    reports through window.FoxTheme.Notification (the stock code
    called this.Notification, which does not exist on this element).
*/

const BHCartRecommendations = {
  timer: null,
  token: 0,

  schedule() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.run(), 60);
  },

  // The most expensive line, by unit price. Returns its product id.
  findAnchorProductId(components) {
    let anchorId = null;
    let best = -1;
    components.forEach((component) => {
      const item = component.closest("[data-cart-item-product-id]");
      if (!item) return;
      const priceEl = item.querySelector("[data-wpd-cart-drawer-variant-price]");
      const price = priceEl
        ? Number(priceEl.getAttribute("data-wpd-cart-drawer-variant-price")) || 0
        : 0;
      if (price > best) {
        best = price;
        anchorId = item.getAttribute("data-cart-item-product-id");
      }
    });
    return anchorId;
  },

  fetchBlock(baseUrl, productId, intent) {
    let url;
    try {
      url = new URL(baseUrl, window.location.origin);
    } catch (e) {
      return Promise.resolve(null);
    }
    if (productId) url.searchParams.set("product_id", productId);
    // Ask for extra so there are still up to 3 left after in-cart products are skipped.
    url.searchParams.set("limit", "6");
    url.searchParams.set("intent", intent);

    return fetch(url.toString())
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector(
          "#shopify-section-cart-recommendations"
        );
        if (
          recommendations &&
          recommendations.querySelectorAll(".f-cart-recommendations__item").length
        ) {
          return recommendations.innerHTML;
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  },

  run() {
    const components = Array.from(
      document.querySelectorAll("cart-recommendations-component")
    );
    if (!components.length) return;

    const target = components[components.length - 1];
    const baseUrl = target.dataset.url;
    if (!baseUrl) return;

    // Only the last component is ever used. Keep the rest empty and hidden.
    components.forEach((component) => {
      if (component !== target) {
        component.innerHTML = "";
        component.style.display = "none";
      }
    });

    const anchorId = this.findAnchorProductId(components);
    const token = ++this.token;

    this.fetchBlock(baseUrl, anchorId, "complementary")
      .then((html) => html || this.fetchBlock(baseUrl, anchorId, "related"))
      .then((html) => {
        // A newer cart render started, or this element was replaced: drop this result.
        if (token !== this.token || !target.isConnected) return;
        if (html) {
          // Show first, then insert, so the slider measures a visible element.
          target.style.display = "";
          target.innerHTML = html;
        } else {
          target.innerHTML = "";
          target.style.display = "none";
        }
      });
  },
};

class CartRecommendationsComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Stay hidden until the coordinator decides this is the one block to show.
    this.style.display = "none";
    BHCartRecommendations.schedule();
  }
}

if (!customElements.get("cart-recommendations-component")) {
  customElements.define(
    "cart-recommendations-component",
    CartRecommendationsComponent
  );
}

if (!customElements.get("add-to-cart-button")) {
  class AddToCartComponent extends HTMLElement {
    constructor() {
      super();

      this.cartDrawer = document.querySelector("cart-drawer");
      this.addToCartButton = this.querySelector(
        ".f-cart-recommendations__item-add-to-cart"
      );
      this.addEventListener("click", this.onSubmitHandler.bind(this));
    }

    onSubmitHandler() {
      const variantId = this.dataset.variantId;

      if (!variantId) return;
      if (!this.addToCartButton) {
        this.addToCartButton = this.querySelector(
          ".f-cart-recommendations__item-add-to-cart"
        );
      }
      if (
        this.addToCartButton &&
        this.addToCartButton.classList.contains("btn--loading")
      ) {
        return;
      }
      if (this.addToCartButton) this.addToCartButton.classList.add("btn--loading");

      const config = {
        method: "POST",
        headers: {
          Accept: "application/javascript",
          "X-Requested-With": "XMLHttpRequest",
        },
      };

      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", 1);

      const cartDrawer = this.cartDrawer || document.querySelector("cart-drawer");
      if (cartDrawer) {
        formData.append(
          "sections",
          cartDrawer.getSectionsToRender().map((section) => section.id)
        );
        formData.append("sections_url", window.location.pathname);
      }

      config.body = formData;

      fetch(`${FoxThemeSettings.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            const notification = window.FoxTheme && window.FoxTheme.Notification;
            if (notification && notification.show) {
              notification.show({
                target: document.body,
                method: "appendChild",
                type: "warning",
                message: response.description,
                last: 3000,
                sticky: true,
              });
            }
            return;
          }
          window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, response);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          if (this.addToCartButton) {
            this.addToCartButton.classList.remove("btn--loading");
          }
        });
    }
  }
  customElements.define("add-to-cart-button", AddToCartComponent);
}
